import type { TranslationKeysProStudio } from '@pypestream/translations';

import {
  splitVisibleChannels,
  type SolutionChannelGroup,
} from '../../utils/group-channels-by-solution';
import type { PlatformChannel } from '../../utils/map-connection-to-row';

/**
 * A plural family is stored as `key_one` / `key_other` (plus whatever CLDR
 * categories a locale has), and i18next is called with the BASE key and a
 * `count`. The generated key union holds only the suffixed keys, so the base is
 * derived from them here rather than written as a string the union cannot check.
 */
type PluralBase<K extends string> =
  K extends `${infer Base}_${'zero' | 'one' | 'two' | 'few' | 'many' | 'other'}`
    ? Base
    : K;

// User-facing strings (PSTUDIO-4343) are i18n keys in `pro-studio/common`, under
// `solutionChannelGroups`. The view model takes a `translate` function and
// applies it to the keys below; the two renderers never format a sentence
// themselves, so a word-order change in a locale is a JSON edit only.
export const SOLUTION_GROUPS_I18N_KEYS = {
  goToSolutionVersionsTooltip:
    'pro-studio/common:solutionChannelGroups.goToSolutionVersionsTooltip',
  goToChannelTooltip:
    'pro-studio/common:solutionChannelGroups.goToChannelTooltip',
  regionLabel: 'pro-studio/common:solutionChannelGroups.regionLabel',
  goToVersionsLabel:
    'pro-studio/common:solutionChannelGroups.goToVersionsLabel',
  goToChannelLabel: 'pro-studio/common:solutionChannelGroups.goToChannelLabel',
  seeMore: 'pro-studio/common:solutionChannelGroups.seeMore',
  seeLess: 'pro-studio/common:solutionChannelGroups.seeLess',
  seeMoreSolutions: 'pro-studio/common:solutionChannelGroups.seeMoreSolutions',
  seeFewerSolutions:
    'pro-studio/common:solutionChannelGroups.seeFewerSolutions',
  noSolution: 'pro-studio/common:solutionChannelGroups.noSolution',
} as const satisfies Record<
  string,
  PluralBase<
    Extract<
      TranslationKeysProStudio,
      `pro-studio/common:solutionChannelGroups.${string}`
    >
  >
>;

export type SolutionGroupsKey =
  (typeof SOLUTION_GROUPS_I18N_KEYS)[keyof typeof SOLUTION_GROUPS_I18N_KEYS];

/**
 * Interpolation values of the keys above. `seeMore` takes `remaining`, not
 * `count`: a `count` option makes i18next look for plural forms, and
 * "See {{remaining}} more" is deliberately a single form in every locale.
 * `seeMoreSolutions` is the one plural family and takes `count` — with a single
 * form it read "See 1 more solutions" (release 29817 browser pass).
 */
export interface SolutionGroupsParams {
  name?: string;
  remaining?: number;
  count?: number;
}

export type SolutionGroupsTranslate = (
  key: SolutionGroupsKey,
  params?: SolutionGroupsParams
) => string;

/** Adapts `useTranslation()`'s `t` to `SolutionGroupsTranslate`. */
export const toSolutionGroupsTranslate =
  (
    t: (key: SolutionGroupsKey, options?: SolutionGroupsParams) => unknown
  ): SolutionGroupsTranslate =>
  (key, params) =>
    String(t(key, params));

/**
 * `test-id` prefixes of the surfaces. `table` and `modal` are kept as they were
 * before the boxes existed, because the e2e reads
 * `ps-live-agent-platforms-channel-pill-*`. The Reporting API and Data Privacy
 * pages (PSTUDIO-4779) each get their own, so a selector of one page never
 * matches a pill of another.
 */
export const SOLUTION_GROUPS_TEST_ID_PREFIX = {
  table: 'ps-live-agent-platforms',
  modal: 'ps-live-agent-platform-modal',
  reportingApi: 'ps-reporting-api-credentials',
  dataPrivacy: 'ps-data-privacy',
} as const;

export type SolutionGroupsTestIdPrefix =
  (typeof SOLUTION_GROUPS_TEST_ID_PREFIX)[keyof typeof SOLUTION_GROUPS_TEST_ID_PREFIX];

/**
 * Key of a box's expanded state. Scoped by owner (a connection id in the table,
 * the edited connection in the modal) AND solution, so expanding one box never
 * expands another connection's box for the same solution.
 */
export const groupToggleKey = (
  ownerId: string,
  group: Pick<SolutionChannelGroup, 'solutionId' | 'unresolved'>
) =>
  `${ownerId}:${group.unresolved ? 'unresolved' : (group.solutionId ?? 'no-solution')}`;

/**
 * One channel pill. `interactive: false` means the pill leads nowhere and is
 * drawn as plain text: no button, no "Go to channel" tooltip or name, no
 * arrow. That is decided by the BOX the pill sits in — the "No solution" box
 * and the unresolved list — because a channel route needs the solution id
 * (`buildChannelRoute`), and in those two boxes there is none to resolve. It
 * is NOT read back from the route on click: other reasons for a missing route
 * (no project id, no channel id) are still absorbed by the click handlers.
 */
export type ChannelPillView = {
  channel: PlatformChannel;
  name: string;
  testId: string;
} & (
  | { interactive: true; ariaLabel: string; tooltip: string }
  | { interactive: false }
);

/** A "See more" / "See less" style toggle, drawn the same wherever it appears. */
export interface MoreToggleView {
  label: string;
  expanded: boolean;
  icon: 'chevrons-down-up' | 'chevrons-up-down';
}

// One rule for the label and the icon of every such toggle: the per-solution
// channel toggle and the per-connection solutions toggle differ only in keys.
const toMoreToggleView = (
  expanded: boolean,
  label: string
): MoreToggleView => ({
  label,
  expanded,
  icon: expanded ? 'chevrons-down-up' : 'chevrons-up-down',
});

export interface SolutionGroupView {
  /** Expanded-state key, see `groupToggleKey`; also the React list key. */
  key: string;
  /**
   * The solutions are not known yet: the group is a flat list of pills with
   * NO chip and NO region (see `SolutionChannelGroup.unresolved`). `name` is
   * then empty and nothing renders it.
   */
  unresolved: boolean;
  name: string;
  /**
   * The region's accessible name, or `null` when the group is no region (the
   * unresolved list). The "No solution" box is named by that phrase alone —
   * "Solution {{name}}" with it substituted read "Solution No solution".
   */
  regionLabel: string | null;
  testId: string;
  /**
   * The chip's link, or `null` for the "No solution" box — whose chip is then
   * plain text, NOT a button: there is no solution to go to — and for the
   * unresolved list, which has no chip at all.
   */
  link: { solutionId: string; ariaLabel: string; tooltip: string } | null;
  pills: ChannelPillView[];
  /** The "See N more" / "See less" toggle, or `null` when nothing overflows. */
  more: MoreToggleView | null;
}

export interface BuildSolutionGroupViewsArgs {
  groups: SolutionChannelGroup[];
  /** Scopes the expanded state, see `groupToggleKey`. */
  ownerId: string;
  isExpanded: (key: string) => boolean;
  testIdPrefix: SolutionGroupsTestIdPrefix;
  /** Resolves a key to the active locale's text, see `toSolutionGroupsTranslate`. */
  translate: SolutionGroupsTranslate;
}

/**
 * Everything both renderers draw, decided once: they only map this to Lit or
 * to JSX, so a label, a test id or the static-vs-link decision cannot differ
 * between the Lit table cell and the React renderer (Edit modal, Data Privacy).
 */
export const buildSolutionGroupViews = ({
  groups,
  ownerId,
  isExpanded,
  testIdPrefix,
  translate,
}: BuildSolutionGroupViewsArgs): SolutionGroupView[] =>
  groups.map((group) => {
    const key = groupToggleKey(ownerId, group);
    const expanded = isExpanded(key);
    const { visible, remaining } = splitVisibleChannels(
      group.channels,
      expanded
    );
    const { solutionId } = group;
    const unresolved = !!group.unresolved;
    // `null` marks the "No solution" box, whose heading is a translated string.
    // The unresolved list has no heading at all.
    const name = unresolved
      ? ''
      : (group.solutionName ?? translate(SOLUTION_GROUPS_I18N_KEYS.noSolution));
    let regionLabel: string | null = null;
    if (!unresolved) {
      regionLabel =
        solutionId == null
          ? name
          : translate(SOLUTION_GROUPS_I18N_KEYS.regionLabel, { name });
    }
    return {
      key,
      unresolved,
      name,
      regionLabel,
      testId: `${testIdPrefix}-solution-${unresolved ? 'unresolved' : (solutionId ?? 'none')}`,
      // An unresolved group has `solutionId: null` too, so it gets no link.
      link:
        solutionId == null
          ? null
          : {
              solutionId,
              ariaLabel: translate(
                SOLUTION_GROUPS_I18N_KEYS.goToVersionsLabel,
                {
                  name,
                }
              ),
              tooltip: translate(
                SOLUTION_GROUPS_I18N_KEYS.goToSolutionVersionsTooltip
              ),
            },
      pills: visible.map((channel): ChannelPillView => {
        const base = {
          channel,
          name: channel.name,
          testId: `${testIdPrefix}-channel-pill-${channel.channelId}`,
        };
        // An unresolved group has `solutionId: null` too; see ChannelPillView.
        if (solutionId == null) return { ...base, interactive: false };
        return {
          ...base,
          interactive: true,
          ariaLabel: translate(SOLUTION_GROUPS_I18N_KEYS.goToChannelLabel, {
            name: channel.name,
          }),
          tooltip: translate(SOLUTION_GROUPS_I18N_KEYS.goToChannelTooltip),
        };
      }),
      more:
        remaining > 0
          ? toMoreToggleView(
              expanded,
              expanded
                ? translate(SOLUTION_GROUPS_I18N_KEYS.seeLess)
                : translate(SOLUTION_GROUPS_I18N_KEYS.seeMore, { remaining })
            )
          : null,
    };
  });

export interface BuildSolutionsToggleViewArgs {
  /** Overflow count from `splitVisibleSolutionGroups`. */
  remaining: number;
  expanded: boolean;
  translate: SolutionGroupsTranslate;
}

/**
 * The connection-level "See N more solutions" / "See fewer solutions" toggle,
 * or `null` when no solution overflows. The expanded state is the caller's.
 */
export const buildSolutionsToggleView = ({
  remaining,
  expanded,
  translate,
}: BuildSolutionsToggleViewArgs): MoreToggleView | null =>
  remaining > 0
    ? toMoreToggleView(
        expanded,
        expanded
          ? translate(SOLUTION_GROUPS_I18N_KEYS.seeFewerSolutions)
          : translate(SOLUTION_GROUPS_I18N_KEYS.seeMoreSolutions, {
              count: remaining,
            })
      )
    : null;
